import React, { useState, useEffect } from 'react';
import { MdPersonAdd } from 'react-icons/md';
import styled from 'styled-components';
import RoomList from './RoomList';
import ChatForm from './ChatForm';
import Conversation from './Conversation';
import { useChat } from '../context/ChatProvider';
import Wrapper from './Wrapper';
import axios from 'axios';
import ShareModal from './ShareModal';
import ModalAlert from './ModalAlert';
import OnlineUsersModal from './OnlineUsersModal';
import useChatActions from '../hooks/useChatActions';
import RoomInviteModal from '../hooks/useRoomInvites';
import { useTranslation } from "react-i18next";
import SubscribeModal from "./SubscribeModal";
import RoomForm from "./RoomForm";
import { AiOutlineBars } from "react-icons/ai";
import LoginModal from "./LoginModal";

/* ─── layout ─── */

const CenterContainer = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 100%;
    margin: auto 0;
    justify-content: space-between;
    min-width: 0;

    @media (max-width: 820px) {
        height: 80%;
    }
`;

const Chat = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 80%;
    width: 100%;
    position: relative;
    background: rgba(15, 12, 41, 0.6);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
`;

const Header = styled.header`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5em;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    padding: 0.6em 1em;
    height: 4em;
    background: rgba(255, 255, 255, 0.03);
    flex-shrink: 0;

    & h2 {
        font-size: 0.9em;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.9);
        max-width: 15em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const ShareIcon = styled(MdPersonAdd)`
    cursor: pointer;
    padding: 6px;
    color: #a5b4fc;
    background: rgba(99, 102, 241, 0.2);
    border: 1px solid rgba(99, 102, 241, 0.3);
    width: 2.4em;
    height: 2.4em;
    border-radius: 50%;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: rgba(99, 102, 241, 0.35);
        border-color: rgba(99, 102, 241, 0.5);
        color: white;
    }
`;

const RoomImagesContainer = styled.div`
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: 0;
`;

const RoomImage = styled.img`
    width: 2.6em;
    height: 2.6em;
    border-radius: 50%;
    margin-left: -10px;
    border: 2px solid rgba(15, 12, 41, 0.8);
    object-fit: cover;
    flex-shrink: 0;

    &:first-child {
        margin-left: 0;
    }
`;

const MenuButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.4em;
    height: 2.4em;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
    color: rgba(255, 255, 255, 0.65);

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
    }

    & svg {
        fill: currentColor;
    }
`;

/* ─── component ─── */

const ChatContainer = () => {
    const { socket, currentRoom, user, isNavOpen, setIsNavOpen, setActiveView, onlineUsers = [], connectionState, getRoomUsers, isMobile, blockSending, setBlockSending, setRooms, rooms, joinRoom, setLoadingMessages, setCurrentRoom, addLanguageToRoom, setIsMessageSending } = useChat();
    const { inviteToRoom } = useChatActions();
    const [showModal, setShowModal] = useState(false);
    const [inviteLink, setInviteLink] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [isOnlineUsersModalOpen, setIsOnlineUsersModalOpen] = useState(false);
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);
    const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [loginModalMessage, setLoginModalMessage] = useState('');

    const { t } = useTranslation();
    const isGuest = user?.guest;
    const GUEST_MESSAGE_QUOTA = 25;

    const generateInvite = async () => {
        try {
            if (isGuest) {
                showLogin("To invite others, you need an account. Create one or sign in below.");
                return;
            }
            if (blockSending) return;
            const response = await axios.get(`${process.env.REACT_APP_SERVER}/gen-invite/${currentRoom.id}`, { withCredentials: true });
            setInviteLink(response.data.link);
            setInviteCode(response.data.lineInviteCode);
            setShowModal(true);
        } catch (err) {
            console.log(err);
        }
    };

    const updateRoomLanguages = async (language) => {
        try {
            addLanguageToRoom(currentRoom.id, language);
        } catch (e) {
            console.log(e);
        }
    };

    const showLogin = (message) => {
        setLoginModalMessage(message);
        setShowLoginModal(true);
    };

    const handleConversationListClick = () => {
        if (!isNavOpen) {
            setIsNavOpen(true);
            setActiveView(isMobile ? "chat" : "roomNav");
        } else {
            setIsNavOpen(false);
            setActiveView("chat");
        }
        window.scrollTo(0, 0);
    };

    const toggleOnlineUsersModal = () => {
        setIsOnlineUsersModalOpen(!isOnlineUsersModalOpen);
    };

    const handleInviteToRoom = async (invitedUserId) => {
        inviteToRoom(invitedUserId, user.id, user.username, currentRoom.id);
    };

    useEffect(() => {
        if (connectionState === 'disconnected') {
            setShowDisconnectModal(true);
        } else {
            setShowDisconnectModal(false);
            if (currentRoom) {
                joinRoom(currentRoom.id);
            }
        }
    }, [connectionState]);

    useEffect(() => {
        window.scrollTo(0, 0);
        document.body.style.overflowY = 'hidden';
        return () => { document.body.style.overflowY = 'auto'; };
    }, []);

    useEffect(() => {
        if (currentRoom) {
            joinRoom(currentRoom.id);
            setActiveView("chat");
            window.scrollTo(0, 0);
        }
    }, [currentRoom]);

    const showQuotaReachedModal = async (message) => {
        if (!message.user) return;
        if (message.user.id === user.id) {
            if (user.guest) {
                showLogin("To continue, please create an account or sign in below.");
            } else {
                setIsSubscribeModalOpen(true);
            }
            setBlockSending(true);
            setIsMessageSending(false);
        }
    };

    useEffect(() => {
        if (socket) {
            socket.on('quota-reached', showQuotaReachedModal);
            return () => { socket.off('quota-reached'); };
        }
    }, [socket]);

    useEffect(() => {
        if (!user.subscriber) {
            if (user.messagesSent >= user.messageQuota) {
                setBlockSending(true);
                setIsSubscribeModalOpen(true);
            }
        }
        if (user.guest) {
            if (user.messagesSent >= GUEST_MESSAGE_QUOTA) {
                setBlockSending(true);
                showLogin("To continue, please create an account or sign in below.");
            }
        }
    }, []);

    const toggleCreateRoomModal = () => {
        if (isGuest) { setShowLoginModal(true); return; }
        if (blockSending) return;
        setIsModalOpen(!isModalOpen);
    };

    const addNewRoom = (newRoom) => {
        setRooms([...rooms, newRoom]);
        if (isGuest) return;
        setLoadingMessages(true);
        setCurrentRoom(newRoom);
        sessionStorage.setItem('currentRoomId', newRoom.id);
        sessionStorage.setItem('currentRoom', JSON.stringify(newRoom));
        const roomLanguages = newRoom.languages;
        socket.emit('join-room', { roomID: newRoom.id, roomLanguages, userJoining: user });
        setActiveView("chat");
        setIsNavOpen(false);
        window.scrollTo(0, 0);
    };

    return (
        <Wrapper enableScrolling={false}>
            <CenterContainer>
                <RoomForm isOpen={isModalOpen} onNewRoom={addNewRoom} onClose={toggleCreateRoomModal} />
                {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} message={loginModalMessage} />}

                <Chat>
                    {!currentRoom ? (
                        <Header>
                            <MenuButton onClick={handleConversationListClick}>
                                <AiOutlineBars size="1.2em" />
                            </MenuButton>
                        </Header>
                    ) : (
                        <>
                            <Header>
                                <MenuButton onClick={handleConversationListClick}>
                                    <AiOutlineBars size="1.2em" />
                                </MenuButton>
                                <h2>{currentRoom.name}</h2>

                                <RoomImagesContainer>
                                    {Array.from(new Set(getRoomUsers(currentRoom.id).map(u => u.avatar)))
                                        .filter(avatar => avatar)
                                        .map((avatar, index) => (
                                            <RoomImage key={avatar} src={avatar} alt={`User ${index}`} />
                                        ))}
                                    {currentRoom.ownerId === user.id && (
                                        <ShareIcon onClick={generateInvite} />
                                    )}
                                </RoomImagesContainer>
                            </Header>

                            <Conversation />
                            <ChatForm />
                        </>
                    )}
                </Chat>

                {showModal && (
                    <ShareModal
                        inviteLink={inviteLink}
                        inviteCode={inviteCode}
                        onClose={() => setShowModal(false)}
                        onLanguageSelected={updateRoomLanguages}
                    />
                )}

                {Array.isArray(onlineUsers) && user && (
                    <OnlineUsersModal currentUserId={user.id} onlineUsers={onlineUsers} onInvite={handleInviteToRoom} isOpen={isOnlineUsersModalOpen} onClose={toggleOnlineUsersModal} />
                )}

                <RoomInviteModal />
                <ModalAlert isOpen={showDisconnectModal} isSuccess={false} title={t('DisconnectedModalTitle')} message={t('DisconnectModalText')} showCloseButton={false} />
                {isSubscribeModalOpen && <SubscribeModal onClose={() => setIsSubscribeModalOpen(false)} userId={user.id} userEmail={user.email} title={t('FTOverModalTitle')} message={t('FTOverModalDesc')} />}
            </CenterContainer>

            {isNavOpen && <RoomList showLoginModal={showLogin} />}
        </Wrapper>
    );
};

export default ChatContainer;
